/* free-gift-sync v1 */
/* ============================================================
   free-gift-sync.js — shared free-gift reconciler for custom
   add-to-cart paths (wishlist "Move to bag").
   ============================================================

   WHY THIS EXISTS
   ---------------
   The free-gift-with-purchase feature lives in theme.min.js
   (`syncGiftProducts`), which auto-adds / removes the gift product
   as the cart total crosses each threshold. It is driven by
   Stiletto's internal `cart:updated` / `quick-cart:updated` pub-sub,
   which only fires for cart mutations that go through Stiletto's own
   cart pipeline.

   The wishlist "Move to bag" flows (wishlist.js, quick-account.js,
   wishlist-account.js) deliberately hit `/cart/add.js` with a raw
   fetch and then re-render the quick-cart by hand (see the long note
   in quick-account.js — a synthetic `CustomEvent('quick-cart:updated')`
   does NOT reach Stiletto's listener). Because of that, the gift sync
   never runs for a wishlist→bag move, so a customer who crosses a gift
   threshold by moving an item from their wishlist never receives the
   free gift. theme.min.js is a module, so its sync isn't reachable on
   `window` either.

   This helper closes that gap. It is ADD-ONLY: a move-to-bag only
   ever RAISES the cart total, so we only need to add newly-qualifying
   gifts — removals stay the responsibility of the bar's own reconcile
   on the normal cart pipeline.

   It reads its config from the SAME element the bar reads
   (`[data-free-shipping-bar]`), so the variant IDs it adds are always
   the exact ones the bar expects — no divergence, no duplicate gifts.

   Exposed as `window.rastahSyncFreeGifts()` → returns a Promise that
   NEVER rejects (a gift hiccup must never break the host cart flow).
   ============================================================ */
(function () {
  'use strict';

  function presentmentRate() {
    var r = window.Shopify && window.Shopify.currency && window.Shopify.currency.rate;
    var n = parseFloat(r);
    return n && !isNaN(n) ? n : 1;
  }

  function toCents(str) {
    if (!str) return 0;
    var n = parseFloat(String(str).replace(',', '.'));
    return isNaN(n) ? 0 : Math.round(n * 100);
  }

  function isExpired(str) {
    if (!str) return false;
    var d = new Date(str);
    return !isNaN(d.getTime()) && d.getTime() < Date.now();
  }

  /* The free-shipping-bar (gift config host) is only injected into the
     page after the quick-cart has been rendered once. When it isn't in
     the live DOM yet, fall back to parsing it out of a fresh quick-cart
     section render. */
  function resolveBar() {
    var live = document.querySelector('[data-free-shipping-bar]');
    if (live) return Promise.resolve(live);
    return fetch('/cart?section_id=quick-cart', { credentials: 'same-origin' })
      .then(function (r) { return r.text(); })
      .then(function (html) {
        return new DOMParser().parseFromString(html, 'text/html')
          .querySelector('[data-free-shipping-bar]');
      })
      .catch(function () { return null; });
  }

  function giftsFromDataset(ds) {
    var slots = [
      ['giftThreshold',  'freeGiftProduct',  'gift1ExpiresAt'],
      ['giftThreshold2', 'freeGiftProduct2', 'gift2ExpiresAt'],
      ['giftThreshold3', 'freeGiftProduct3', 'gift3ExpiresAt'],
      ['giftThreshold4', 'freeGiftProduct4', 'gift4ExpiresAt']
    ];
    var gifts = [];
    slots.forEach(function (s) {
      var threshold = ds[s[0]];
      var product = ds[s[1]];
      if (!threshold || !product) return;
      if (isExpired(ds[s[2]])) return;
      var variantId = parseInt(product, 10);
      var thresholdCents = toCents(threshold);
      if (variantId && thresholdCents) {
        gifts.push({ variantId: variantId, thresholdCents: thresholdCents });
      }
    });
    return gifts;
  }

  /* Add-only reconcile. Adds any qualifying gift not already in the
     cart, one at a time (sequential so the cart can't double-add).
     Resolves when done; never rejects. */
  window.rastahSyncFreeGifts = function rastahSyncFreeGifts() {
    return resolveBar().then(function (bar) {
      if (!bar) return;
      var gifts = giftsFromDataset(bar.dataset);
      if (!gifts.length) return;
      var rate = presentmentRate();

      return fetch('/cart.js', { credentials: 'same-origin', headers: { 'Accept': 'application/json' } })
        .then(function (res) { return res.json(); })
        .then(function (cart) {
          // Convert presentment total -> shop cents so thresholds (shop
          // currency) compare correctly under multi-currency.
          var storeCents = rate ? Math.round(cart.total_price / rate) : cart.total_price;
          var inCart = {};
          cart.items.forEach(function (it) { inCart[it.id] = true; });

          var chain = Promise.resolve();
          gifts.forEach(function (g) {
            if (storeCents < g.thresholdCents || inCart[g.variantId]) return;
            chain = chain.then(function () {
              return fetch('/cart/add.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
                body: JSON.stringify({ id: g.variantId, quantity: 1 })
              }).catch(function () { /* sold out / unavailable — skip */ });
            });
          });
          return chain;
        });
    }).catch(function () { /* never break the host cart flow */ });
  };
})();
